<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="mb-4">
          <a :href="`/businesses/${businessId}`" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to {{ businessName }}
          </a>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Locations
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ businessName }}
            </p>
          </div>
          <button
            @click="openCreateForm"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Location
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 max-w-sm">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search locations..."
          class="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          @input="debouncedSearch">
      </div>

      <div v-if="loading" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div v-for="n in 5" :key="n" class="animate-pulse flex space-x-4 mb-4">
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      </div>

      <div v-else-if="locations.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <LocationsTable :locations="locations" @view="navigateToLocation" />
      </div>

      <div v-else class="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          No locations
        </h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add your first location for this business.
        </p>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Page {{ currentPage }} of {{ totalPages }}
        </p>
        <div class="flex space-x-2">
          <button
            :disabled="currentPage <= 1"
            @click="currentPage--; fetchLocations()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button
            :disabled="currentPage >= totalPages"
            @click="currentPage++; fetchLocations()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getBusinessLocations } from '~/javascript/api/locations.js'
import LocationsTable from '~/javascript/components/dashboard/components/locations_table.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import LocationForm from './form.vue'

const props = defineProps({
  businessId: { type: String, required: true },
  businessName: { type: String, default: '' }
})

const locations = ref([])
const loading = ref(true)
const searchQuery = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const slidePanel = ref(null)

const fetchLocations = async() => {
  try {
    loading.value = true
    const params = { page: currentPage.value }
    if (searchQuery.value) params.q = searchQuery.value

    const response = await getBusinessLocations(props.businessId, params)
    locations.value = response.data
    totalPages.value = parseInt(response.headers['total-pages'] || '1', 10)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching locations:', error)
  } finally {
    loading.value = false
  }
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    fetchLocations()
  }, 300)
}

const navigateToLocation = (location) => {
  window.location.href = `/businesses/${props.businessId}/locations/${location.id}`
}

const onLocationSaved = () => {
  slidePanel.value.close()
  fetchLocations()
}

const openCreateForm = () => {
  slidePanel.value.open({
    component: LocationForm,
    props: { businessId: props.businessId },
    events: { saved: onLocationSaved }
  })
}

onMounted(() => {
  hideLoadingSpinner()
  fetchLocations()
})
</script>
